import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Ruler, Building, Users, HardHat, Award, CheckCircle, Clock, Leaf } from 'lucide-react';
import { companyInfo, stats, whyChooseUs } from '../data/mock';
import ProjectCarousel from '../components/ProjectCarousel';
import ClientsCarousel from '../components/ClientsCarousel';
import ServicesCarousel from '../components/ServicesCarousel';
import SEO from '../components/SEO';

const Home = () => {
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Ayati Constructions - Home",
    "description": "Leading construction contractor in Hyderabad specializing in high-rise buildings, commercial complexes, and residential projects.",
    "url": "https://www.ayaticonstructions.com/",
    "mainEntity": {
      "@type": "Organization",
      "name": "Ayati Constructions and Estates LLP"
    }
  };

  return (
    <div className="home">
      <SEO 
        title="Home"
        description="Ayati Constructions - Hyderabad's trusted construction contractor specializing in high-rise buildings. 200+ professionals, 2 Cr+ sq.ft built, projects worth 2000+ Crores. Quality, Safety, Sustainability."
        keywords="construction contractor Hyderabad, high-rise construction, building contractor Hyderabad, Ayati Constructions, commercial construction Telangana, residential construction India, Mivan formwork contractor"
        canonicalUrl="/"
        structuredData={homeStructuredData}
      />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <img
            src="https://images.unsplash.com/photo-1689574666903-ec23039b3558?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxoaWdoLXJpc2UlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzY4NjM1MDAyfDA&ixlib=rb-4.1.0&q=75&w=1920"
            alt="High-rise construction"
            className="hero-image"
            fetchPriority="high"
            decoding="async" />

          <div className="hero-overlay"></div>
        </div>
        
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="hero-title">{companyInfo.tagline}</h1>
            <p className="hero-subtitle">Leading construction contractors specializing in high-rise buildings, delivering excellence in every project through innovation, quality, and precision


            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn-primary">
                Request a Quote
                <ArrowRight size={20} />
              </Link>
              <a href={`tel:${companyInfo.phone}`} className="btn-secondary">
                <Phone size={20} />
                Call Now: {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => {
              const iconMap = {
                Ruler,
                Building,
                Users,
                HardHat
              };
              const IconComponent = iconMap[stat.icon] || Building;
              return (
                <div key={index} className="stat-card">
                  <IconComponent className="stat-icon" size={40} />
                  <h3 className="stat-value">
                    {stat.value}
                    {stat.unit && <span className="stat-unit"> {stat.unit}</span>}
                  </h3>
                  <p className="stat-label">{stat.label}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview">
        <div className="container">
          <div className="about-preview-grid">
            <div className="about-preview-image">
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc2ODU3OTY3NHww&ixlib=rb-4.1.0&q=75&w=1000"
                alt="Construction team at work"
                loading="lazy"
                decoding="async" />

            </div>
            <div className="about-preview-content">
              <span className="section-label">About Us</span>
              <h2 className="section-title">Building Trust Through Quality Construction</h2>
              
              <div className="mission-vision-preview">
                <div className="mv-preview-item">
                  <h3 className="mv-preview-title">Our Mission</h3>
                  <p className="section-description">
                    {companyInfo.mission}
                  </p>
                </div>
                
                <div className="mv-preview-item">
                  <h3 className="mv-preview-title">Our Vision</h3>
                  <p className="section-description">
                    {companyInfo.vision}
                  </p>
                </div>
              </div>
              
              <Link to="/about" className="btn-text">
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="our-services-home">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">Our Services</span>
            <h2 className="section-title">What We Do</h2>
            <p className="section-description">
              Comprehensive construction solutions for every project, from high-rise buildings to residential complexes
            </p>
          </div>

          <ServicesCarousel />

          <div className="text-center" style={{ marginTop: '3rem' }}>
            <Link to="/services" className="btn-outline">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title">Your Trusted Construction Partner</h2>
            <p className="section-description">
              With over 15 years of experience and 150+ successful projects, we bring unmatched expertise to every build
            </p>
          </div>

          <div className="features-grid">
            {whyChooseUs.map((feature, index) => {
              const iconMap = {
                Users,
                CheckCircle,
                Clock,
                Leaf
              };
              const IconComponent = iconMap[feature.icon] || Users;
              return (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="featured-projects">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-label">Our Work</span>
              <h2 className="section-title">Featured Projects</h2>
            </div>
            <Link to="/projects" className="btn-text">
              View All Projects
              <ArrowRight size={18} />
            </Link>
          </div>

          <ProjectCarousel />
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="our-clients-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-label">Our Clients</span>
            <h2 className="section-title">Trusted by Leading Organizations</h2>
            <p className="section-description">
              We're proud to partner with industry leaders and deliver exceptional construction services
            </p>
          </div>

          <ClientsCarousel />
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Construction Project?</h2>
            <p className="cta-description">
              Get in touch with our expert team for a free consultation and quote
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary-large">
                Get Free Quote
              </Link>
              <a href={`tel:${companyInfo.phone}`} className="btn-secondary-large">
                <Phone size={20} />
                Call: {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;